import * as React from 'react';
import { useEffect, useState } from 'react';
import { Redirect, useLocation, useRouteMatch } from 'react-router-dom';
import { Link } from 'theme-ui';
import trackEvent from '../../../utils/analytics';
import { useAuth } from '../../hooks/use-auth';
import {
  Box,
  Loading,
  backgroundTransition,
  contentTransition,
  ArrowRightIcon,
} from '../../ui';
import Header from '../header/Header';
import Preview from '../preview/Preview';
import Share from '../share/Share';
import Write from '../write/Write';
import { useActiveResume } from 'v1/context/resume-context';
import { useUser } from 'v1/hooks/use-user';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResumeEditor = () => {
  const [isAnonymousUpgrade, setIsAnonymousUpgrade] = useState(false);
  const auth = useAuth();
  const { url } = useRouteMatch();
  const user = useUser();
  const query = useQuery();
  const resume = useActiveResume();

  // Handle checkout success redirects from Stripe
  useEffect(() => {
    const stripesuccess = query.get('stripesuccess');
    const amount = parseInt(query.get('amount') || '19') ?? 19;
    if (stripesuccess) {
      trackEvent.gaPurchase(amount);
      if (auth && auth.user && auth.user.isAnonymous) {
        setIsAnonymousUpgrade(true);
      }
    }
  }, []); // eslint-disable-line

  const shouldCreateAccount = user && user.isAnonymous;
  const previewActive = !!useRouteMatch('/resumes/:resumeId/preview');
  const shareActive = !!useRouteMatch('/resumes/:resumeId/share');
  const writeActive = !!useRouteMatch('/resumes/:resumeId/write');

  // Redirect to resume/:resumeId/write from resume/:resumeId
  if (!writeActive && !previewActive && !shareActive) {
    return <Redirect to={`${url}/write${window.location.search}`} />;
  }

  if (!resume) {
    return (
      <LoadingContainer isDark={previewActive}>
        <Loading />
      </LoadingContainer>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'grid',
        gridTemplateRows: shouldCreateAccount
          ? 'auto auto minmax(0, 1fr)'
          : 'auto minmax(0, 1fr)',
        gridTemplateColumns: '1fr',
        pl: 3,
        pr: [3, 3, 0],
        backgroundColor: previewActive ? 'surfaceGrayDark' : 'surfaceGray',
        transition: `background-color ${backgroundTransition}`,
        '> *': {
          mx: 'auto !important',
          maxWidth: 'appContainerWidth',
        },
      }}
    >
      <Header />
      {shouldCreateAccount && <CreateAccountBanner />}
      <AnimationWrapper isVisible={writeActive}>
        <Write resume={resume} />
      </AnimationWrapper>
      <AnimationWrapper isVisible={previewActive}>
        <Preview resume={resume} isVisible={previewActive} />
      </AnimationWrapper>
      <AnimationWrapper isVisible={shareActive}>
        <Share resume={resume} isVisible={shareActive} />
      </AnimationWrapper>
    </Box>
  );
};

type AnimationWrapperProps = {
  children: React.ReactNode;
  isVisible: boolean;
};

const AnimationWrapper = ({ children, isVisible }: AnimationWrapperProps) => (
  <Box
    sx={{
      position: isVisible ? 'relative' : 'absolute',
      width: '100%',
      height: isVisible ? '100%' : 0,
      overflow: isVisible ? 'auto' : 'hidden',
      opacity: isVisible ? 1 : 0,
      transition: `opacity ${contentTransition}`,
    }}
  >
    {children}
  </Box>
);

const LoadingContainer = ({
  children,
  isDark,
}: {
  children: React.ReactNode;
  isDark: boolean;
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: isDark ? 'surfaceGrayDark' : 'surfaceGray',
        minHeight: '100vh',
        height: '100vh',
        width: '100%',
        overflowX: 'hidden',
        transition: `background-color ${backgroundTransition}`,
      }}
    >
      {children}
    </Box>
  );
};

function CreateAccountBanner() {
  return (
    <Box sx={{ pr: [0, 0, 3], width: '100%' }}>
      <Link
        href="/v2/setup/account?fromapp=true"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          py: 2,
          bg: 'blue',
          color: 'white',
          textDecoration: 'none',
          transition: 'background 300ms linear',
          ':hover': {
            bg: 'blue80',
          },
        }}
      >
        <Box sx={{ mr: 2, color: 'white' }}>
          To save your resume{' '}
          <Box
            as="span"
            sx={{
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            Sign up
          </Box>
        </Box>
        <ArrowRightIcon color="white" />
      </Link>
    </Box>
  );
}
export default ResumeEditor;
